import React, { FC, useMemo } from "react";
import { Box, View, ALIGN, useDevice, Spinner, InfoBox, INFOBOX_TYPE } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Layout, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ProductVariant } from "../../../../../domain/projections/Wishlist";
import { Shape } from "../../../components/shape/Shape";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { WishlistHeader } from "../header/wishlistHeader/WishlistHeader";
import { LookAndLikeProducts } from "../productDetail/lookAndLikeProducts/LookAndLikeProducts";
import { style as wishlistStyle } from "./Wishlist.styles";
import { WishlistProductList } from "./wishlistProductList/WishlistProductList";

interface WishlistProps {
  readonly layout: Layout;
  readonly isLoading: boolean;
  readonly productVariants: ProductVariant[];
  readonly hasBasketNotification: boolean;
  readonly onAddToBasket: (variantId: string) => void;
  readonly onBack: () => void;
  readonly onDeleteFromWishlist: (variantId: string) => void;
  readonly onPressCart: () => void;
}

const Wishlist: FC<WishlistProps> = ({
  layout: Layout,
  isLoading,
  hasBasketNotification,
  productVariants,
  onBack,
  onPressCart,
  onDeleteFromWishlist,
  onAddToBasket,
}) => {
  const { screen } = useDevice();
  const { tradename } = useStaticInfo();

  const whishlistTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_TITLE,
  });
  const whistlistInformationText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_INFORMATION_TEXT,
  });
  const itemNotAvailableText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ITEM_NOT_AVAILABLE,
  });
  const whishlistEmptyStateTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_EMPTY_STATE_TITLE,
  });
  const whishlistEmptyStateText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_EMPTY_STATE_TEXT,
  });
  const emptyStateCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_STATE_CTA,
  });

  const style = useMemo(() => wishlistStyle(), []);

  const [availableProducts, unavailableProducts] = useMemo(() => {
    if (isLoading) {
      return [[], []];
    }

    return productVariants.reduce(
      (acc, currentProduct) => {
        const [currentAvailable, currentUnavailable] = acc;

        if (currentProduct.available) {
          currentAvailable.push(currentProduct);
        } else {
          currentUnavailable.push(currentProduct);
        }

        return [currentAvailable, currentUnavailable] as [ProductVariant[], ProductVariant[]];
      },
      [[], []] as [ProductVariant[], ProductVariant[]],
    );
  }, [isLoading, productVariants]);

  return (
    <Layout
      footer={null}
      header={
        <WishlistHeader
          hasBasketNotification={hasBasketNotification}
          title={`${whishlistTitleText} (${productVariants.length})`}
          onBack={onBack}
          onPressCart={onPressCart}
        />
      }
      style={{
        scrollView: screen.L ? style.desktopLayout : undefined,
      }}
    >
      {isLoading ? (
        <View style={style.centeredContainer}>
          <Spinner />
        </View>
      ) : (
        <>
          {productVariants.length > 0 ? (
            <>
              <View style={screen.L ? style.desktopInfobox : style.mobileInfobox}>
                <InfoBox
                  alignItems={ALIGN.CENTER}
                  contentAlign={screen.L ? ALIGN.CENTER : undefined}
                  icon="heart"
                  text={whistlistInformationText}
                  type={screen.L ? INFOBOX_TYPE.SQUARED : INFOBOX_TYPE.ROUNDED}
                />
              </View>
              <Box align={ALIGN.CENTER} size={{ L: "1/3" }}>
                <WishlistProductList
                  products={availableProducts}
                  rounded={screen.L}
                  onAddToBasket={onAddToBasket}
                  onDeleteFromWishlist={onDeleteFromWishlist}
                />
                <WishlistProductList
                  products={unavailableProducts}
                  rounded={screen.L}
                  style={screen.L ? style.desktopBottomList : undefined}
                  title={`${itemNotAvailableText} (${unavailableProducts.length})`}
                  unavailable
                  onAddToBasket={() => undefined}
                  onDeleteFromWishlist={onDeleteFromWishlist}
                />
              </Box>
            </>
          ) : (
            <View style={style.emptyStateWrapper}>
              <View style={style.emptyStateContainer}>
                {tradename === Tradename.LOOKIERO && <Shape type="doubleHalf" />}
                <Box
                  align={ALIGN.CENTER}
                  size={{ L: "1/3" }}
                  style={[
                    style.emptyWishlistMessage,
                    screen.L ? style.emptyWishlistMessageLarge : style.emptyWishlistMessageSmall,
                  ]}
                >
                  <Text level={3} variant="heading">
                    {whishlistEmptyStateTitleText}
                  </Text>
                  <Text level={3} style={style.emptyWishlistMessageBody}>
                    {whishlistEmptyStateText}
                  </Text>
                  <Button style={{ button: style.emptyWishlistMessageButton }} onPress={onBack}>
                    {emptyStateCtaText}
                  </Button>
                </Box>

                {tradename === Tradename.LOOKIERO && <Shape type="rectangle" />}
              </View>

              <View style={style.moduleWrapper}>
                <LookAndLikeProducts fullWidth={false} />
              </View>
            </View>
          )}
        </>
      )}
    </Layout>
  );
};

export { Wishlist };
